:root {
  color: #15314c;
  background: #ffffff;
  font-family: "Inter", "Segoe UI", Arial, sans-serif;
  font-size: 16px;
  line-height: 1.45;
  text-rendering: geometricPrecision;
}

/* cod5 final polish: compact internal pages without changing the legal brand tone */
.officeInternal,
.simpleInternal,
.lawyersInternal,
.clientsOnly,
.contactInternal,
.articlePage {
  min-height: auto;
}

.heroSection,
.section,
.internalPage,
.articlePage {
  padding-top: clamp(72px, 8vw, 108px);
  padding-bottom: clamp(72px, 8vw, 108px);
}

.officeBand,
.contactBand,
.contactInternal {
  gap: clamp(32px, 5vw, 54px);
}

.experienceBand,
.serviceCard,
.teamCard,
.contactPanel,
.articleAside {
  box-shadow: 0 18px 46px rgba(27, 42, 57, 0.10);
}

@media (max-width: 760px) {
  .officeInternal,
  .simpleInternal,
  .lawyersInternal,
  .clientsOnly,
  .contactInternal,
  .articlePage {
    min-height: auto;
  }
}

* {
  box-sizing: border-box;
}

body {
  margin: 0;
  background: #ffffff;
}

a {
  color: inherit;
  text-decoration: none;
}

img {
  display: block;
  max-width: 100%;
}

.siteHeader {
  width: min(1140px, calc(100% - 48px));
  margin: 0 auto;
  min-height: 138px;
  display: grid;
  grid-template-columns: 318px 1fr;
  justify-content: center;
  align-items: center;
  gap: 24px;
  border-bottom: 1px solid #eee7dd;
}

.brand img {
  width: 318px;
  height: auto;
}

nav {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  color: #33393d;
  font-size: 0.74rem;
  font-weight: 800;
  text-transform: uppercase;
}

nav a {
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 0 14px;
  background: #f7efe5;
  color: #263541;
  box-shadow: 0 8px 18px rgba(27, 42, 57, 0.06);
  transition: background .2s ease, border-color .2s ease, color .2s ease;
}

nav a:hover {
  border-color: #decdb8;
  background: #f3eadf;
  color: #b68653;
}

.heroSection {
  min-height: 1010px;
  padding: 112px max(40px, calc((100vw - 1140px) / 2)) 178px;
  display: grid;
  grid-template-columns: minmax(0, 565px) minmax(340px, 1fr);
  gap: 72px;
  align-items: start;
  background: radial-gradient(circle at 73% 4%, rgba(220, 202, 180, 0.42), rgba(255, 255, 255, 0) 30%),
    linear-gradient(110deg, #ffffff 0%, #fbf7f1 59%, #f3eadf 100%);
}

.kicker {
  margin: 0 0 15px;
  color: #bd8a55;
  font-size: 0.69rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

h1,
h2,
h3,
p {
  margin-top: 0;
}

h1,
h2,
h3 {
  font-family: Georgia, "Times New Roman", serif;
  color: #163451;
  font-weight: 500;
  letter-spacing: 0;
}

h1 {
  max-width: 550px;
  margin-bottom: 22px;
  font-size: 3.95rem;
  line-height: 0.98;
}

h2 {
  margin-bottom: 12px;
  font-size: 2.55rem;
  line-height: 1.06;
}

h3 {
  margin-bottom: 10px;
  font-size: 1.35rem;
  line-height: 1.08;
}

.lead,
.sectionHeader p,
.officeIntro p,
.contactBand p {
  color: #5e6870;
  font-size: 0.96rem;
}

.heroActions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 32px;
}

.button {
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  padding: 0 20px;
  font-size: 0.78rem;
  font-weight: 800;
  box-shadow: 0 16px 28px rgba(19, 49, 76, 0.08);
}

.button.primary {
  background: #092c48;
  color: #ffffff;
}

.button.ghost {
  border: 1px solid #e1ded8;
  background: #ffffff;
  color: #152f49;
}

.metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 13px;
  margin-top: 46px;
  max-width: 610px;
}

.metrics div,
.featureCard,
.practiceCard,
.teamCard,
.clientCard,
.postCard,
.contactCard {
  border: 1px solid #ece5dc;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 22px 44px rgba(27, 42, 57, 0.08);
}

.metrics div {
  min-height: 104px;
  border-radius: 12px;
  padding: 18px 17px;
}

.metrics strong {
  display: block;
  margin-bottom: 7px;
  color: #113656;
  font-size: 1.35rem;
}

.metrics span,
.featureCard p,
.practiceCard p,
.teamCard p,
.postCard p,
.contactCard p,
.contactCard small {
  color: #66717a;
  font-size: 0.82rem;
}

.heroCards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  padding-top: 24px;
}

.featureCard {
  border-radius: 12px;
  padding: 24px;
}

.featureCard.large {
  grid-column: 1 / -1;
  min-height: 224px;
}

.featureCard span,
.contactCard span {
  display: inline-flex;
  margin-bottom: 12px;
  border-radius: 999px;
  background: #efe4d4;
  padding: 5px 11px;
  color: #b17a40;
  font-size: 0.65rem;
  font-weight: 800;
  text-transform: uppercase;
}

.featureCard a,
.officeIntro a,
.postCard a {
  color: #092c48;
  font-size: 0.78rem;
  font-weight: 900;
}

.section {
  padding: 142px max(40px, calc((100vw - 1140px) / 2));
}

.sectionHeader {
  max-width: 730px;
  margin-bottom: 34px;
}

.practiceGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
}

.practiceCard {
  min-height: 188px;
  border-radius: 14px;
  padding: 30px 28px 26px;
  position: relative;
  overflow: hidden;
}

.practiceCard::after {
  content: "";
  position: absolute;
  inset: auto 18px 0 auto;
  width: 72px;
  height: 72px;
  border: 1px solid #efe4d4;
  border-radius: 50%;
  transform: translateY(36px);
}

.practiceCard span {
  display: inline-flex;
  margin-bottom: 18px;
  color: #bd8a55;
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.08em;
}

.officeBand {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 430px;
  gap: 78px;
  background: #f5efe6;
}

.officeIntro {
  align-self: center;
}

.officeIntro a {
  display: inline-flex;
  margin-top: 8px;
}

.pillarList {
  display: grid;
  gap: 24px;
}

.teamGrid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 18px;
}

.teamCard {
  min-height: 296px;
  border-radius: 14px;
  padding: 24px 22px;
}

.teamCard span {
  width: 43px;
  height: 43px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  border-radius: 50%;
  background: #092c48;
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 800;
}

.teamCard h3 {
  min-height: 88px;
  margin-bottom: 8px;
  font-size: 1.25rem;
}

.teamCard strong {
  display: block;
  margin-bottom: 8px;
  color: #bd8a55;
  font-size: 0.74rem;
}

.centerAction {
  display: flex;
  justify-content: center;
  margin-top: 54px;
}

.clientsSection {
  padding-top: 118px;
}

.clientGrid {
  min-height: 315px;
  display: grid;
  grid-template-columns: repeat(5, minmax(150px, 1fr));
  gap: 18px;
  align-items: center;
}

.clientCard:nth-child(n) {
  grid-column: auto;
}

.clientCard {
  min-height: 168px;
  border-radius: 12px;
  padding: 22px 18px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 18px;
}

.clientCard img {
  width: min(178px, 96%);
  max-height: 94px;
  object-fit: contain;
}

.clientCard span {
  color: #3b444d;
  font-size: 0.72rem;
  font-weight: 800;
}

.postsGrid {
  display: grid;
  grid-template-columns: 1.45fr 0.7fr;
  grid-template-rows: auto auto;
  gap: 24px;
  align-items: start;
}

.postCard {
  overflow: hidden;
  border-radius: 14px;
}

.postCard.large {
  grid-row: span 2;
}

.postCard img {
  width: 100%;
  height: 292px;
  object-fit: cover;
  object-position: center top;
}

.postCard.large img {
  height: 730px;
}

.postCard div {
  padding: 28px 26px 30px;
}

.postCard time {
  display: block;
  margin-bottom: 13px;
  color: #bd8a55;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
}

.contactBand {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 430px;
  gap: 70px;
  background: #f7f1e8;
}

.contactBand ul {
  margin: 24px 0 0;
  padding-left: 18px;
  color: #4d5861;
  font-size: 0.9rem;
}

.contactBand li + li {
  margin-top: 8px;
}

.contactCard {
  border-radius: 14px;
  padding: 42px 38px 38px;
}

.contactCard .button {
  margin: 20px 0 30px;
}

footer {
  width: min(1140px, calc(100% - 48px));
  min-height: 236px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 auto;
  color: #27323a;
  font-size: 0.74rem;
}

footer img {
  width: 306px;
}

.pageHero {
  min-height: 360px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 14px;
  padding: 70px 24px;
  background: linear-gradient(110deg, #f5efe6, #ffffff);
  text-align: center;
}

.pageHero h1 {
  margin-bottom: 0;
}

.pageHero p {
  margin: 0;
  color: #bd8a55;
  font-size: 0.82rem;
}

.internalPage {
  padding: 118px max(40px, calc((100vw - 1140px) / 2));
}

.textBlock {
  max-width: 850px;
}

.textBlock p,
.internalPage p,
.articlePage p {
  color: #5e6870;
}

.officeInternal {
  min-height: auto;
}

.imagePair {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px;
  margin: 52px 0;
}

.imagePair img {
  width: 100%;
  min-height: 390px;
  object-fit: cover;
}

.valueGrid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
}

.valuesBlock {
  margin-top: 46px;
  max-width: 850px;
  border: 1px solid #ece5dc;
  border-radius: 14px;
  padding: 38px;
  background: #f7f1e8;
}

.valuesBlock div {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 22px;
}

.valuesBlock span {
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0 16px;
  background: #ffffff;
  color: #b17a40;
  font-size: 0.72rem;
  font-weight: 900;
  text-transform: uppercase;
}

.experienceBand {
  max-width: 820px;
  margin-top: 95px;
  padding: 46px;
  background: #f5efe6;
}

.experienceBand strong {
  display: block;
  margin-bottom: 18px;
  color: #bd8a55;
  font-size: 0.78rem;
  text-transform: uppercase;
}

.simpleInternal {
  min-height: auto;
}

.lawyersInternal {
  min-height: auto;
}

.internalTeam {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-top: 34px;
}

.internalTeam .teamCard {
  min-height: 470px;
}

.teamCard ul {
  margin: 16px 0 0;
  padding-left: 18px;
  color: #66717a;
  font-size: 0.82rem;
}

.teamCard li + li {
  margin-top: 8px;
}

.clientsOnly {
  min-height: auto;
  text-align: center;
}

.clientsOnly h4 {
  color: #bd8a55;
  font-size: 1.1rem;
}

.listing {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.contactInternal {
  min-height: auto;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 420px;
  gap: 70px;
}

.contactForm {
  display: grid;
  gap: 14px;
  margin-top: 28px;
}

.contactForm input,
.contactForm textarea,
.commentFields input,
.commentBlock textarea {
  width: 100%;
  border: 1px solid #ece5dc;
  border-radius: 12px;
  padding: 16px 18px;
  font: inherit;
  color: #15314c;
  background: #ffffff;
}

.contactForm textarea,
.commentBlock textarea {
  min-height: 180px;
  resize: vertical;
}

.contactForm button,
.commentBlock button {
  min-height: 42px;
  justify-self: start;
  border: 0;
  border-radius: 999px;
  padding: 0 24px;
  background: #092c48;
  color: #ffffff;
  font-weight: 800;
}

.contactInternal aside {
  align-self: start;
  padding: 36px;
  border: 1px solid #ece5dc;
  border-radius: 14px;
  background: #f7f1e8;
}

.mapFrame {
  overflow: hidden;
  margin-top: 24px;
  border: 1px solid #e6dacb;
  border-radius: 12px;
  background: #ffffff;
}

.mapFrame iframe {
  width: 100%;
  min-height: 320px;
  display: block;
  border: 0;
}

.articlePage {
  width: min(900px, calc(100% - 48px));
  min-height: auto;
  margin: 0 auto;
  padding: 112px 0 130px;
}

.articlePage h1 {
  max-width: 100%;
}

.articlePage time {
  display: block;
  margin-bottom: 34px;
  color: #bd8a55;
  font-size: 0.76rem;
  font-weight: 800;
  text-transform: uppercase;
}

.commentBlock {
  margin-top: 78px;
  padding-top: 46px;
  border-top: 1px solid #ece5dc;
}

.commentBlock h2 {
  font-size: 1.75rem;
}

.commentFields {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 14px 0;
}

@media (max-width: 920px) {
  .siteHeader {
    width: calc(100% - 32px);
    grid-template-columns: 1fr;
    min-height: auto;
    padding: 22px 0;
    gap: 16px;
  }

  nav {
    width: 100%;
    justify-content: center;
    flex-wrap: nowrap;
    gap: 8px;
    overflow-x: auto;
    white-space: nowrap;
    padding: 10px;
    border: 1px solid #efe4d4;
    border-radius: 16px;
    background: #fbf7f1;
  }

  .heroSection,
  .officeBand,
  .contactBand,
  .contactInternal {
    grid-template-columns: 1fr;
  }

  .heroSection,
  .section {
    padding-left: 24px;
    padding-right: 24px;
  }

  .practiceGrid,
  .teamGrid,
  .postsGrid,
  .valueGrid,
  .imagePair,
  .commentFields {
    grid-template-columns: 1fr;
  }

  .clientGrid {
    grid-template-columns: repeat(2, minmax(135px, 1fr));
    gap: 16px;
  }

  .clientCard:nth-child(n) {
    grid-column: auto;
  }

  .postCard.large {
    grid-row: auto;
  }

  .postCard.large img,
  .postCard img {
    height: 280px;
  }

  .internalPage {
    padding-left: 24px;
    padding-right: 24px;
  }

  .officeInternal {
    min-height: auto;
  }

  .lawyersInternal {
    min-height: auto;
  }

  .contactInternal {
    min-height: auto;
  }

  .articlePage {
    min-height: auto;
  }
}

@media (max-width: 560px) {
  .siteHeader {
    width: calc(100% - 36px);
  }

  .brand img,
  footer img {
    width: 222px;
  }

  h1 {
    font-size: 2.55rem;
  }

  h2 {
    font-size: 2rem;
  }

  .heroSection {
    min-height: auto;
    padding-top: 58px;
    padding-bottom: 76px;
  }

  .metrics,
  .heroCards {
    grid-template-columns: 1fr;
  }

  .clientGrid {
    grid-template-columns: 1fr;
  }

  .heroSection {
    min-height: auto;
    padding-top: 40px;
    padding-bottom: 58px;
  }

  h1 {
    font-size: 2.7rem;
  }

  h2 {
    font-size: 2.15rem;
  }

  .metrics,
  .heroCards,
  .clientGrid {
    grid-template-columns: 1fr;
  }

  .teamCard {
    min-height: auto;
  }

  .teamCard h3 {
    min-height: auto;
  }

  footer {
    width: calc(100% - 36px);
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    gap: 24px;
  }
}
